import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Loader2, 
  AlertTriangle,
  CheckCircle,
  FileDown,
  Eye,
  Sparkles,
  Scale,
  Shield,
  Building,
  MapPin,
  Calendar,
  User,
  AlertCircle,
  TrendingUp,
  Home,
  Gavel,
  FileCheck,
  XCircle,
  ChevronRight,
  Info,
  Edit,
  ArrowRight,
  PoundSterling,
  History,
  Check,
  X,
  Plus,
  Trash2,
  Save,
  Pencil,
  Lock,
  Unlock,
  ShieldCheck,
  Clock
} from 'lucide-react';

// Room types for accommodation
const ROOM_TYPES = [
  { value: 'bedroom', label: 'Bedroom' },
  { value: 'master_bedroom', label: 'Master Bedroom' },
  { value: 'double_bedroom', label: 'Double Bedroom' },
  { value: 'single_bedroom', label: 'Single Bedroom' },
  { value: 'box_room', label: 'Box Room' },
  { value: 'reception', label: 'Reception' },
  { value: 'living_room', label: 'Living Room' },
  { value: 'lounge', label: 'Lounge' },
  { value: 'sitting_room', label: 'Sitting Room' },
  { value: 'dining_room', label: 'Dining Room' },
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'kitchen_diner', label: 'Kitchen/Diner' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'shower_room', label: 'Shower Room' },
  { value: 'en_suite', label: 'En-Suite' },
  { value: 'wc', label: 'WC' },
  { value: 'cloakroom', label: 'Cloakroom' },
  { value: 'utility', label: 'Utility' },
  { value: 'study', label: 'Study' },
  { value: 'home_office', label: 'Home Office' },
  { value: 'conservatory', label: 'Conservatory' },
  { value: 'garage', label: 'Garage' },
  { value: 'hall', label: 'Hall' },
  { value: 'landing', label: 'Landing' },
  { value: 'loft_room', label: 'Loft Room' },
  { value: 'basement', label: 'Basement' },
  { value: 'other', label: 'Other' }
];

const FLOOR_OPTIONS = [
  { value: 'Basement', label: 'Basement' },
  { value: 'Lower Ground', label: 'Lower Ground' },
  { value: 'Ground', label: 'Ground' },
  { value: 'First', label: 'First' },
  { value: 'Second', label: 'Second' },
  { value: 'Third', label: 'Third' },
  { value: 'Fourth', label: 'Fourth' },
  { value: 'Loft', label: 'Loft/Attic' }
];
import ReportVersionHistory from './ReportVersionHistory';
import ScreenshotExtractor from './ScreenshotExtractor';
import ComparableAnalysisMatrix from './ComparableAnalysisMatrix';
import InvestmentReportIntelligence from './InvestmentReportIntelligence';
import { getErrorMessage } from '../utils/errorUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Number to words helper
const numberToWords = (num) => {
  if (num === 0) return 'zero';
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  
  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? '-' + ones[n % 10] : '');
    if (n < 1000) return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
    if (n < 1000000) return convert(Math.floor(n / 1000)) + ' thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    return convert(Math.floor(n / 1000000)) + ' million' + (n % 1000000 ? ' ' + convert(n % 1000000) : '');
  };
  return convert(num);
};

// Report type configurations
const REPORT_TYPES = [
  {
    value: 'rics_red_book',
    label: 'Standard RICS Red Book Valuation',
    description: 'Full compliance with RICS Valuation Global Standards (AJA Hybrid Format)',
    icon: Shield,
    badgeColor: 'bg-green-500',
    textColor: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-500'
  },
  {
    value: 'probate',
    label: 'Probate Valuation',
    description: 'For Inheritance Tax / HMRC purposes',
    icon: FileText,
    badgeColor: 'bg-amber-500',
    textColor: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-500'
  },
  {
    value: 'rics_red_book_cpr35',
    label: 'RICS Red Book – Expert Witness (CPR 35)',
    description: 'Red Book compliant with CPR Part 35 requirements for litigation',
    icon: Gavel,
    badgeColor: 'bg-purple-500',
    textColor: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-500'
  },
  {
    value: 'matrimonial',
    label: 'Matrimonial / Family Proceedings',
    description: 'For divorce and family court proceedings',
    icon: User,
    badgeColor: 'bg-pink-500',
    textColor: 'text-pink-700',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-500'
  },
  {
    value: 'help_to_buy',
    label: 'Help to Buy Equity Loan',
    description: 'For Help to Buy scheme equity calculations',
    icon: Home,
    badgeColor: 'bg-blue-500',
    textColor: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-500'
  },
  {
    value: 'shared_ownership',
    label: 'Shared Ownership / Staircasing',
    description: 'For Housing Association staircasing and resale',
    icon: Building,
    badgeColor: 'bg-teal-500',
    textColor: 'text-teal-700',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-500'
  },
  {
    value: 'survey_level_2',
    label: 'Survey Report (Level 2)',
    description: 'RICS Home Survey Level 2 HomeBuyer format',
    icon: Home,
    badgeColor: 'bg-sky-500',
    textColor: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-500'
  },
  {
    value: 'survey_level_3',
    label: 'Survey Report (Level 3)',
    description: 'RICS Home Survey Level 3 Building format',
    icon: Building,
    badgeColor: 'bg-sky-600',
    textColor: 'text-sky-700',
    bgColor: 'bg-sky-50',
    borderColor: 'border-sky-600'
  },
  {
    value: 'expert_witness_standalone',
    label: 'Expert Witness – Standalone',
    description: 'CPR 35 compliant expert opinion',
    icon: Scale,
    badgeColor: 'bg-purple-600',
    textColor: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-600'
  }
];

const BASIS_OF_VALUE = [
  { value: 'market_value', label: 'Market Value' },
  { value: 'market_rent', label: 'Market Rent' },
  { value: 'investment_value', label: 'Investment Value' },
  { value: 'fair_value', label: 'Fair Value' },
  { value: 'equitable_value', label: 'Equitable Value' }
];

const VALUATION_PURPOSES = [
  { value: 'loan_security', label: 'Loan Security / Mortgage' },
  { value: 'probate', label: 'Probate / Inheritance Tax' },
  { value: 'lease_extension', label: 'Lease Extension' },
  { value: 'enfranchisement', label: 'Enfranchisement' },
  { value: 'commercial_investment', label: 'Commercial Investment' },
  { value: 'development_appraisal', label: 'Development Appraisal' },
  { value: 'matrimonial', label: 'Matrimonial / Divorce' },
  { value: 'litigation', label: 'Litigation / Dispute' },
  { value: 'general_advice', label: 'General Advisory' }
];

export default function ReportGeneratorDialog({ open, onClose, inspection, onSuccess }) {
  const { token, user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [validating, setValidating] = useState(false);
  const [activeTab, setActiveTab] = useState('type');
  
  // Report configuration
  const [reportType, setReportType] = useState('rics_red_book');
  const [clientName, setClientName] = useState('');
  const [basisOfValue, setBasisOfValue] = useState('market_value');
  const [valuationPurpose, setValuationPurpose] = useState('loan_security');
  const [valuationDate, setValuationDate] = useState(new Date().toISOString().split('T')[0]);
  const [inspectionDate, setInspectionDate] = useState('');
  const [reportFormat, setReportFormat] = useState('pdf');
  const [isDraft, setIsDraft] = useState(true);
  const [versionNotes, setVersionNotes] = useState('');
  
  // Professional letter format fields
  const [reportReference, setReportReference] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientAddresseeName, setClientAddresseeName] = useState('');
  const [propertyTypeDescription, setPropertyTypeDescription] = useState('Commercial and Residential Valuation Report');
  const [tenureType, setTenureType] = useState('freehold');
  const [titleNumber, setTitleNumber] = useState('');
  const [leaseTerm, setLeaseTerm] = useState('');
  const [rentalValue, setRentalValue] = useState('');
  const [capitalValue, setCapitalValue] = useState('');
  const [allRiskYield, setAllRiskYield] = useState('');
  const [pros, setPros] = useState([]);
  const [cons, setCons] = useState([]);
  const [newPro, setNewPro] = useState('');
  const [newCon, setNewCon] = useState('');
  
  // CPR 35 specific fields
  const [instructingParty, setInstructingParty] = useState('');
  const [natureOfDispute, setNatureOfDispute] = useState('');
  const [questionsToAddress, setQuestionsToAddress] = useState('');
  
  // Valuer declaration
  const [valuerName, setValuerName] = useState('');
  const [valuerQualification, setValuerQualification] = useState('MRICS');
  
  // Editable report sections (for validation fixes)
  const [valuationMethod, setValuationMethod] = useState('');
  const [valuationMethodDescription, setValuationMethodDescription] = useState('');
  const [valuationRationale, setValuationRationale] = useState('');
  const [judgementCommentary, setJudgementCommentary] = useState('');
  const [marketEvidence, setMarketEvidence] = useState('');
  const [marketValue, setMarketValue] = useState('');
  const [piInsurance, setPiInsurance] = useState('Professional Indemnity insurance is held with cover adequate for this instruction');
  
  // AI generation state
  const [generatingAI, setGeneratingAI] = useState(false);
  
  // Validation state
  const [validationResult, setValidationResult] = useState(null);
  const [complianceReport, setComplianceReport] = useState(null);
  const [editSection, setEditSection] = useState(null); // Track which section to highlight
  
  // Accommodation editing state
  const [accommodation, setAccommodation] = useState([]);
  const [editingAccommodation, setEditingAccommodation] = useState(false);
  const [savingAccommodation, setSavingAccommodation] = useState(false);
  
  // Valuation sign-off status
  const [valuationStatus, setValuationStatus] = useState(null);
  const [loadingValuationStatus, setLoadingValuationStatus] = useState(false);
  
  // Construction details (property description)
  const [wallType, setWallType] = useState('');
  const [roofType, setRoofType] = useState('');
  const [windowType, setWindowType] = useState('');
  const [approximateAge, setApproximateAge] = useState('');
  const [floorAreaSqm, setFloorAreaSqm] = useState('');
  const [constructionNotes, setConstructionNotes] = useState('');
  
  // Accommodation CRUD functions
  const addRoom = () => {
    setAccommodation([...accommodation, {
      id: Date.now(),
      name: `Room ${accommodation.length + 1}`,
      type: 'bedroom',
      floor: 'Ground',
      length: 0,
      width: 0
    }]);
    setEditingAccommodation(true);
  };
  
  const updateRoom = (index, field, value) => {
    const updated = [...accommodation];
    updated[index] = { ...updated[index], [field]: value };
    setAccommodation(updated);
  };
  
  const removeRoom = (index) => {
    setAccommodation(accommodation.filter((_, i) => i !== index));
  };
  
  const saveAccommodation = async () => {
    setSavingAccommodation(true);
    try {
      // Update property_details with new accommodation
      const updatedPropertyDetails = {
        ...propertyDetails,
        accommodation: accommodation
      };
      
      await axios.put(`${API}/inspections/${inspection.id}`, {
        property_details: updatedPropertyDetails
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPropertyDetails(updatedPropertyDetails);
      setEditingAccommodation(false);
      toast.success('Accommodation schedule saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save accommodation'));
    } finally {
      setSavingAccommodation(false);
    }
  };
  
  const cancelAccommodationEdit = () => {
    // Reset to original data
    setAccommodation(propertyDetails?.accommodation || []);
    setEditingAccommodation(false);
  };
  
  // Check ID to section mapping
  const CHECK_SECTION_MAP = {
    'RB001': { tab: 'details', section: 'instructions', field: 'client-name-input' },
    'RB002': { tab: 'details', section: 'instructions', field: 'purpose-select' },
    'RB003': { tab: 'details', section: 'instructions', field: 'basis-select' },
    'RB004': { tab: 'details', section: 'instructions', field: 'valuation-date-input' },
    'RB005': { tab: 'details', section: 'instructions', field: 'inspection-date-input' },
    'RB006': { tab: 'details', section: 'instructions', field: 'conflicts' },
    'RB007': { tab: 'details', section: 'basis', field: 'basis-definition' },
    'RB008': { tab: 'details', section: 'methodology', field: 'valuation-method' },
    'RB009': { tab: 'details', section: 'rationale', field: 'valuation-rationale' },
    'RB010': { tab: 'details', section: 'conclusion', field: 'market-value' },
    'RB011': { tab: 'details', section: 'compliance', field: 'compliance-statement' },
    'RB012': { tab: 'details', section: 'valuer', field: 'valuer-qualification' },
    'RB013': { tab: 'details', section: 'valuer', field: 'pi-insurance' },
    'RB014': { tab: 'details', section: 'valuer', field: 'independence' },
    'CPR001': { tab: 'details', section: 'cpr35', field: 'instructing-party-input' },
    'CPR002': { tab: 'details', section: 'cpr35', field: 'dispute-textarea' },
    'CPR003': { tab: 'details', section: 'cpr35', field: 'questions-textarea' },
    'CPR004': { tab: 'details', section: 'cpr35-duty', field: 'duty-statement' },
    'CPR005': { tab: 'details', section: 'cpr35-docs', field: 'documents' },
    'CPR006': { tab: 'details', section: 'cpr35-facts', field: 'facts' },
    'CPR007': { tab: 'details', section: 'cpr35-truth', field: 'statement-of-truth' },
    'CPR008': { tab: 'details', section: 'cpr35-independence', field: 'independence-declaration' },
  };
  
  // Handle clicking on a failed validation item
  const handleFixIssue = (checkId) => {
    const mapping = CHECK_SECTION_MAP[checkId];
    if (mapping) {
      setEditSection(mapping.section);
      setActiveTab(mapping.tab);
      // Scroll to field after tab change
      setTimeout(() => {
        const element = document.querySelector(`[data-testid="${mapping.field}"]`) || 
                       document.querySelector(`[data-section="${mapping.section}"]`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.focus?.();
        }
      }, 300);
    }
  };
  
  // AI-powered content generation
  const generateAIContent = async () => {
    if (!complianceReport?.id) {
      toast.error('Create a compliance report first');
      return;
    }
    
    if (!valuationMethod) {
      toast.error('Please select a valuation method first');
      return;
    }
    
    setGeneratingAI(true);
    try {
      const response = await axios.post(
        `${API}/compliance/reports/${complianceReport.id}/generate-rationale`,
        {
          valuation_method: valuationMethod,
          market_value: marketValue ? parseFloat(marketValue) : 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const { rationale, judgement, market_evidence } = response.data.generated;
        setValuationRationale(rationale || '');
        setJudgementCommentary(judgement || '');
        setMarketEvidence(market_evidence || '');
        toast.success('AI content generated - review and edit as needed');
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate AI content'));
    } finally {
      setGeneratingAI(false);
    }
  };
  
  // Data sources
  const [propertyDetails, setPropertyDetails] = useState({});
  const [comparables, setComparables] = useState([]);
  const [amenitiesData, setAmenitiesData] = useState(null);
  
  useEffect(() => {
    if (open && inspection) {
      setInspectionDate(inspection.inspection_date?.split('T')[0] || new Date().toISOString().split('T')[0]);
      setPropertyDetails(inspection.property_details || {});
      setComparables(inspection.property_details?.comparables || []);
      setAccommodation(inspection.property_details?.accommodation || []);
      setValuerName(user?.full_name || user?.email || '');
      // Load saved compliance data from inspection
      if (inspection.client_address) setClientAddress(inspection.client_address);
      if (inspection.valuation_method_description) setValuationMethodDescription(inspection.valuation_method_description);
      if (inspection.client_name) setClientName(inspection.client_name);
      if (inspection.client_addressee_name) setClientAddresseeName(inspection.client_addressee_name);
      setEditingAccommodation(false);

      // Sync: populate construction details from inspection property_details first.
      // These are always applied; fetchExistingReport may override with saved report values.
      const pd = inspection.property_details;
      if (pd) {
        if (pd.wall_construction) setWallType(pd.wall_construction);
        if (pd.roof_type) {
          const roofMap = {
            'pitched+concrete_tiles': 'pitched_concrete',
            'pitched+clay_tiles': 'pitched_tile',
            'pitched+slate': 'pitched_slate',
            'flat+felt': 'flat_felt',
            'flat+membrane': 'flat_membrane',
            'flat+grp': 'flat_grp',
          };
          const key = `${pd.roof_type}+${pd.roof_covering || ''}`;
          setRoofType(roofMap[key] || 'pitched_tile');
        }
        if (pd.window_type || pd.glazing_type) setWindowType(pd.window_type || pd.glazing_type);
        if (pd.age || pd.building_age_id) {
          const ageVal = pd.age || pd.building_age_id;
          const num = parseInt(ageVal, 10);
          if (!isNaN(num)) {
            if (num < 1900) setApproximateAge('pre_1900');
            else if (num < 1930) setApproximateAge('1900_1929');
            else if (num < 1950) setApproximateAge('1930_1949');
            else if (num < 1970) setApproximateAge('1950_1969');
            else if (num < 1990) setApproximateAge('1970_1989');
            else if (num < 2010) setApproximateAge('1990_2009');
            else setApproximateAge('2010_present');
          } else {
            setApproximateAge(String(ageVal));
          }
        }
        if (pd.gross_internal_area) setFloorAreaSqm(String(pd.gross_internal_area));
        if (pd.property_interest) setTenureType(pd.property_interest);
        if (pd.additional_notes) setConstructionNotes(pd.additional_notes);
      }

      fetchAmenities();
      fetchValuationStatus();
      // Async: load existing report (overrides construction with saved report values if present)
      fetchExistingReport();
    }
  }, [open, inspection]);
  
  // Fetch existing compliance report for this inspection
  const fetchExistingReport = async () => {
    if (!inspection?.id) return;
    try {
      const response = await axios.get(`${API}/compliance/reports/inspection/${inspection.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // If reports exist, load the most recent one
      if (response.data && response.data.length > 0) {
        const report = response.data[0]; // Most recent
        setComplianceReport(report);
        
        // Populate form fields from saved report
        if (report.report_type) setReportType(report.report_type);
        
        // Instructions & Scope
        const scope = report.instructions_scope || {};
        if (scope.client_name) setClientName(scope.client_name);
        if (scope.purpose_of_valuation) setValuationPurpose(scope.purpose_of_valuation);
        if (scope.basis_of_value) setBasisOfValue(scope.basis_of_value);
        if (scope.valuation_date) setValuationDate(scope.valuation_date.split('T')[0]);
        if (scope.inspection_date) setInspectionDate(scope.inspection_date.split('T')[0]);
        
        // Executive Summary
        const summary = report.executive_summary_section || {};
        if (summary.client_reference || report.report_reference) setReportReference(summary.client_reference || report.report_reference);
        if (summary.client_addressee_name) setClientAddresseeName(summary.client_addressee_name);
        if (summary.property_type_description) setPropertyTypeDescription(summary.property_type_description);
        if (summary.tenure_type) setTenureType(summary.tenure_type);
        if (summary.title_number) setTitleNumber(summary.title_number);
        if (summary.lease_term) setLeaseTerm(summary.lease_term);
        if (summary.rental_value) setRentalValue(summary.rental_value.toString());
        if (summary.capital_value) setCapitalValue(summary.capital_value.toString());
        if (summary.all_risk_yield) setAllRiskYield(summary.all_risk_yield.toString());
        if (summary.pros) setPros(summary.pros);
        if (summary.cons) setCons(summary.cons);
        
        // Client Address
        if (report.client_address) setClientAddress(report.client_address);
        
        // Valuation Methodology
        const methodology = report.valuation_methodology || {};
        if (methodology.method) setValuationMethod(methodology.method);
        if (methodology.method_description) setValuationMethodDescription(methodology.method_description);
        
        // Valuation Rationale
        const rationale = report.valuation_rationale || {};
        if (rationale.logical_justification) setValuationRationale(rationale.logical_justification);
        if (rationale.judgement_commentary) setJudgementCommentary(rationale.judgement_commentary);
        if (rationale.market_evidence_summary) setMarketEvidence(rationale.market_evidence_summary);
        
        // Valuation Conclusion
        const conclusion = report.valuation_conclusion || {};
        if (conclusion.market_value) setMarketValue(conclusion.market_value.toString());
        if (conclusion.rental_value) setRentalValue(conclusion.rental_value.toString());
        
        // Valuer Declaration
        const declaration = report.valuer_declaration || {};
        if (declaration.valuer_name) setValuerName(declaration.valuer_name);
        if (declaration.valuer_qualification) setValuerQualification(declaration.valuer_qualification);
        if (declaration.pi_insurance_confirmation !== undefined) setPiInsurance(declaration.pi_insurance_confirmation);
        
        // Expert Witness fields (CPR 35)
        if (report.instructing_party) setInstructingParty(report.instructing_party);
        if (report.nature_of_dispute) setNatureOfDispute(report.nature_of_dispute);
        if (report.questions_to_address) setQuestionsToAddress(report.questions_to_address.join('\n'));
        
        // Construction details from saved report override inspection values
        const construction = report.construction_details || {};
        if (construction.wall_type) setWallType(construction.wall_type);
        if (construction.roof_type) setRoofType(construction.roof_type);
        if (construction.window_type) setWindowType(construction.window_type);
        if (construction.approximate_age) setApproximateAge(construction.approximate_age);
        if (construction.floor_area_sqm) setFloorAreaSqm(construction.floor_area_sqm.toString());
        if (construction.notes) setConstructionNotes(construction.notes);
      }
    } catch (err) {
      // No existing report - that's fine, user will create new
      console.log('No existing report found');
    }
  };
  
  const fetchAmenities = async () => {
    try {
      const response = await axios.get(`${API}/amenities/inspection/${inspection.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAmenitiesData(response.data);
    } catch (err) {
      // Amenities not available
    }
  };
  
  // Fetch valuation sign-off status
  const fetchValuationStatus = async () => {
    if (!inspection?.id) return;
    setLoadingValuationStatus(true);
    try {
      const response = await axios.get(`${API}/valuation-core/inspection/${inspection.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.exists) {
        setValuationStatus(response.data.valuation_core);
      } else {
        setValuationStatus(null);
      }
    } catch (err) {
      // Valuation core may not exist yet - that's okay
      setValuationStatus(null);
    } finally {
      setLoadingValuationStatus(false);
    }
  };
  
  const isExpertWitnessMode = reportType === 'rics_red_book_cpr35' || reportType === 'expert_witness_standalone';
  const isValuationReport = reportType === 'rics_red_book' || reportType === 'rics_red_book_cpr35';
  const selectedReportType = REPORT_TYPES.find(t => t.value === reportType);
  
  const createComplianceReport = async () => {
    // Pre-flight validation — show soft errors instead of crashing
    const missing = [];
    if (!basisOfValue) missing.push('Basis of Value (VPS 4)');
    if (!valuationDate || isNaN(new Date(valuationDate).getTime())) missing.push('Valuation Date');
    if (!inspectionDate || isNaN(new Date(inspectionDate).getTime())) missing.push('Inspection Date');
    if (!clientName?.trim()) missing.push('Client Name');
    if (!valuationPurpose) missing.push('Purpose of Valuation');
    if (missing.length > 0) {
      toast.error(`Required fields missing: ${missing.join(', ')}`);
      setActiveTab('details');
      return;
    }

    setGenerating(true);
    try {
      // First, save the compliance data to the inspection for persistence
      await axios.put(`${API}/inspections/${inspection.id}`, {
        client_address: clientAddress,
        valuation_method_description: valuationMethodDescription,
        client_name: clientName,
        client_addressee_name: clientAddresseeName
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const payload = {
        inspection_id: inspection.id,
        report_type: reportType,
        client_name: clientName,
        purpose_of_valuation: valuationPurpose,
        basis_of_value: basisOfValue || 'market_value',
        valuation_date: new Date(valuationDate).toISOString(),
        inspection_date: new Date(inspectionDate).toISOString(),
        // Professional format fields
        report_reference: reportReference,
        client_address: clientAddress,
        // Construction details
        construction_details: {
          wall_type: wallType,
          roof_type: roofType,
          window_type: windowType,
          approximate_age: approximateAge,
          floor_area_sqm: floorAreaSqm ? parseFloat(floorAreaSqm) : null,
          tenure: tenureType,
          notes: constructionNotes
        },
        executive_summary_section: {
          client_reference: reportReference,
          client_addressee_name: clientAddresseeName,
          client_addressee_title: 'Dear',
          property_type_description: propertyTypeDescription,
          tenure_type: tenureType,
          title_number: titleNumber,
          lease_term: leaseTerm,
          rental_value: rentalValue ? parseFloat(rentalValue) : null,
          capital_value: capitalValue ? parseFloat(capitalValue) : null,
          all_risk_yield: allRiskYield ? parseFloat(allRiskYield) : null,
          pros: pros,
          cons: cons
        }
      };
      
      if (isExpertWitnessMode) {
        payload.instructing_party = instructingParty;
        payload.nature_of_dispute = natureOfDispute;
        payload.questions_to_address = questionsToAddress.split('\n').filter(q => q.trim());
      }
      
      const response = await axios.post(`${API}/compliance/reports`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setComplianceReport(response.data.report);
      toast.success('Compliance report created');
      setActiveTab('preview');
      
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create report'));
    } finally {
      setGenerating(false);
    }
  };
  
  // Update compliance report with additional fields
  const updateComplianceReport = async () => {
    if (!complianceReport?.id) {
      toast.error('Create a report first');
      return;
    }
    
    setGenerating(true);
    try {
      // First, save the compliance data to the inspection for persistence (including construction details)
      await axios.put(`${API}/inspections/${inspection.id}`, {
        client_address: clientAddress,
        valuation_method_description: valuationMethodDescription,
        client_name: clientName,
        client_addressee_name: clientAddresseeName,
        property_details: {
          ...propertyDetails,
          wall_type: wallType,
          roof_type: roofType,
          window_type: windowType,
          approximate_age: approximateAge,
          floor_area_sqm: floorAreaSqm ? parseFloat(floorAreaSqm) : null,
          tenure: tenureType
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const updates = {
        valuation_methodology: {
          method: valuationMethod,
          method_description: valuationMethodDescription
        },
        valuation_rationale: {
          logical_justification: valuationRationale,
          judgement_commentary: judgementCommentary,
          market_evidence_summary: marketEvidence
        },
        // Construction details
        construction_details: {
          wall_type: wallType,
          roof_type: roofType,
          window_type: windowType,
          approximate_age: approximateAge,
          floor_area_sqm: floorAreaSqm ? parseFloat(floorAreaSqm) : null,
          tenure: tenureType,
          notes: constructionNotes
        },
        valuation_conclusion: marketValue ? {
          market_value: parseFloat(marketValue),
          rental_value: rentalValue ? parseFloat(rentalValue) : null,
          market_value_words: `${numberToWords(parseInt(marketValue))} pounds`,
          effective_date: valuationDate && !isNaN(new Date(valuationDate).getTime()) ? new Date(valuationDate).toISOString() : new Date().toISOString()
        } : null,
        valuer_declaration: {
          valuer_name: valuerName,
          valuer_qualification: valuerQualification,
          pi_insurance_confirmation: piInsurance,
          independence_statement: "I confirm that this valuation has been prepared independently and impartially"
        },
        // Executive summary updates
        report_reference: reportReference,
        client_address: clientAddress,
        executive_summary_section: {
          client_reference: reportReference,
          client_addressee_name: clientAddresseeName,
          client_addressee_title: 'Dear',
          property_type_description: propertyTypeDescription,
          tenure_type: tenureType,
          title_number: titleNumber,
          lease_term: leaseTerm,
          rental_value: rentalValue ? parseFloat(rentalValue) : null,
          capital_value: capitalValue || marketValue ? parseFloat(capitalValue || marketValue) : null,
          all_risk_yield: allRiskYield ? parseFloat(allRiskYield) : null,
          pros: pros,
          cons: cons
        }
      };
      
      await axios.put(`${API}/compliance/reports/${complianceReport.id}`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEditSection(null);
      toast.success('Report updated');
      
      // Re-validate after update
      await validateCompliance();
      
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update report'));
    } finally {
      setGenerating(false);
    }
  };
  
  const validateCompliance = async () => {
    if (!complianceReport?.id) {
      toast.error('Create a report first');
      return;
    }
    
    setValidating(true);
    try {
      const response = await axios.post(
        `${API}/compliance/reports/${complianceReport.id}/validate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setValidationResult(response.data.validation);
      
      if (response.data.is_compliant) {
        toast.success('Report passes all compliance checks');
      } else {
        toast.warning(`${response.data.validation.blocking_issues.length} compliance issues found`);
      }
      
    } catch (err) {
      toast.error('Validation failed');
    } finally {
      setValidating(false);
    }
  };
  
  const generateAndDownload = async () => {
    if (!complianceReport?.id) {
      toast.error('Create a report first');
      return;
    }
    
    // Pre-flight validation for required Red Book fields
    const missing = [];
    if (!basisOfValue) missing.push('Basis of Value (VPS 4)');
    if (!valuationDate || isNaN(new Date(valuationDate).getTime())) missing.push('Valuation Date');
    if (!inspectionDate || isNaN(new Date(inspectionDate).getTime())) missing.push('Inspection Date');
    if (missing.length > 0) {
      toast.error(`Cannot generate: ${missing.join(', ')} must be defined`);
      setActiveTab('details');
      return;
    }
    
    // Run validation and check result directly (avoid stale closure)
    setValidating(true);
    let valResult = null;
    try {
      const response = await axios.post(
        `${API}/compliance/reports/${complianceReport.id}/validate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      valResult = response.data.validation;
      setValidationResult(valResult);
      if (!response.data.is_compliant) {
        toast.warning(`${valResult.blocking_issues.length} compliance issues found`);
      }
    } catch (err) {
      toast.error('Validation failed — resolve issues before generating');
      setValidating(false);
      return;
    }
    setValidating(false);
    
    if (valResult && valResult.blocking_issues?.length > 0) {
      toast.error('Cannot generate report with compliance issues');
      setActiveTab('validate');
      return;
    }
    
    setGenerating(true);
    try {
      // Generate PDF/Word with versioning
      const response = await axios.post(
        `${API}/compliance/reports/${complianceReport.id}/generate`,
        { 
          format: reportFormat, 
          is_draft: isDraft,
          save_version: true,
          version_notes: versionNotes
        },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Get version info from headers
      const versionNumber = response.headers['x-version-number'] || '1';
      
      const blob = new Blob([response.data], { 
        type: reportFormat === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const statusSuffix = isDraft ? '_DRAFT' : '_FINAL';
      a.download = `${reportType.toUpperCase()}_Report_v${versionNumber}${statusSuffix}_${inspection.property_address?.replace(/[^a-z0-9]/gi, '_')}.${reportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success(`Report v${versionNumber} ${isDraft ? '(Draft)' : '(Final)'} generated and downloaded`);
      setVersionNotes(''); // Clear notes after generation
      onSuccess?.();
      
    } catch (err) {
      // Fallback - just show success for now as export service may not be fully connected
      toast.info('Report created - PDF export coming soon');
    } finally {
      setGenerating(false);
    }
  };
  
  /**
   * Handle data extracted from screenshot
   */
  const handleScreenshotData = (extractedData) => {
    if (extractedData.report_reference) {
      // Could be used for a reference field if we had one
      console.log('Report reference extracted:', extractedData.report_reference);
    }
    
    if (extractedData.purpose_of_valuation) {
      // Try to match to our purpose dropdown options
      const purposeText = extractedData.purpose_of_valuation.toLowerCase();
      if (purposeText.includes('loan') || purposeText.includes('security') || purposeText.includes('mortgage')) {
        setValuationPurpose('loan_security');
      } else if (purposeText.includes('purchase') || purposeText.includes('acquisition')) {
        setValuationPurpose('purchase');
      } else if (purposeText.includes('sale') || purposeText.includes('disposal')) {
        setValuationPurpose('sale');
      } else if (purposeText.includes('probate') || purposeText.includes('estate')) {
        setValuationPurpose('probate');
      } else if (purposeText.includes('matrimonial') || purposeText.includes('divorce')) {
        setValuationPurpose('matrimonial');
      } else if (purposeText.includes('tax') || purposeText.includes('cgt')) {
        setValuationPurpose('cgt');
      } else if (purposeText.includes('account') || purposeText.includes('financial')) {
        setValuationPurpose('accounts');
      }
    }
    
    if (extractedData.rental_value) {
      // Update property details with rental value
      setPropertyDetails(prev => ({
        ...prev,
        rental_value: extractedData.rental_value
      }));
    }
    
    if (extractedData.capital_value) {
      // Update valuation amount in property details
      setPropertyDetails(prev => ({
        ...prev,
        valuation_amount: extractedData.capital_value
      }));
    }
  };
  
  const canProceed = () => {
    if (!clientName) return false;
    if (!valuationDate || !inspectionDate) return false;
    if (isExpertWitnessMode && (!instructingParty || !natureOfDispute)) return false;
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col overflow-hidden" data-testid="report-generator-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-accent" />
            RICS Compliance Report Generator
          </DialogTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <span>{inspection?.property_address}</span>
            {selectedReportType && (
              <Badge className={`${selectedReportType.badgeColor} text-white ml-2`}>
                {isExpertWitnessMode ? 'CPR 35 Mode' : 'Red Book Compliant'}
              </Badge>
            )}
            {/* Valuation Sign-Off Status Indicator */}
            {loadingValuationStatus ? (
              <Badge variant="outline" className="ml-2 animate-pulse">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Checking...
              </Badge>
            ) : valuationStatus ? (
              valuationStatus.status === 'signed_off' ? (
                <Badge 
                  variant="outline" 
                  className="ml-2 bg-green-50 text-green-700 border-green-300"
                  data-testid="valuation-status-locked"
                >
                  <Lock className="h-3 w-3 mr-1" />
                  Valuation Locked
                  {valuationStatus.signed_off_by_name && (
                    <span className="ml-1 text-xs opacity-75">
                      by {valuationStatus.signed_off_by_name}
                    </span>
                  )}
                </Badge>
              ) : valuationStatus.status === 'pending_review' ? (
                <Badge 
                  variant="outline" 
                  className="ml-2 bg-amber-50 text-amber-700 border-amber-300"
                  data-testid="valuation-status-pending"
                >
                  <Clock className="h-3 w-3 mr-1" />
                  Pending Review
                </Badge>
              ) : valuationStatus.status === 'superseded' ? (
                <Badge 
                  variant="outline" 
                  className="ml-2 bg-gray-50 text-gray-600 border-gray-300"
                  data-testid="valuation-status-superseded"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Superseded
                </Badge>
              ) : (
                <Badge 
                  variant="outline" 
                  className="ml-2 bg-blue-50 text-blue-700 border-blue-300"
                  data-testid="valuation-status-draft"
                >
                  <Unlock className="h-3 w-3 mr-1" />
                  Draft - Editable
                </Badge>
              )
            ) : null}
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <TabsList className="grid w-full grid-cols-5 flex-shrink-0">
            <TabsTrigger value="type" data-testid="tab-type">
              <FileCheck className="h-4 w-4 mr-2" />
              Report Type
            </TabsTrigger>
            <TabsTrigger value="details" data-testid="tab-details">
              <FileText className="h-4 w-4 mr-2" />
              Details
            </TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </TabsTrigger>
            <TabsTrigger value="validate" data-testid="tab-validate">
              <Shield className="h-4 w-4 mr-2" />
              Validate
            </TabsTrigger>
            <TabsTrigger value="versions" data-testid="tab-versions">
              <History className="h-4 w-4 mr-2" />
              Versions
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : (
            <div className="flex-1 mt-4 overflow-y-auto" style={{ maxHeight: 'calc(95vh - 180px)' }}>
              <div className="pr-4">
              {/* TAB 1: Report Type Selection */}
              <TabsContent value="type" className="space-y-6 pb-4 mt-0">
                <div>
                  <Label className="text-base font-semibold mb-4 block">Select Report Type *</Label>
                  <div className="grid grid-cols-1 gap-3">
                    {REPORT_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = reportType === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setReportType(type.value)}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            isSelected
                              ? `${type.borderColor} ${type.bgColor}`
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          data-testid={`report-type-${type.value}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-lg ${isSelected ? type.badgeColor : 'bg-gray-100'}`}>
                              <Icon className={`h-6 w-6 ${isSelected ? 'text-white' : 'text-gray-500'}`} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold">{type.label}</p>
                                {type.value.includes('cpr35') || type.value === 'expert_witness_standalone' ? (
                                  <Badge variant="outline" className="text-purple-600 border-purple-300">CPR 35</Badge>
                                ) : type.value.includes('rics') ? (
                                  <Badge variant="outline" className="text-green-600 border-green-300">Red Book</Badge>
                                ) : null}
                              </div>
                              <p className="text-sm text-muted-foreground">{type.description}</p>
                            </div>
                            {isSelected && <CheckCircle className={`h-5 w-5 ${type.textColor}`} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Compliance Mode Indicator */}
                <Card className={isExpertWitnessMode ? 'border-purple-300 bg-purple-50' : 'border-green-300 bg-green-50'}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {isExpertWitnessMode ? (
                        <>
                          <Gavel className="h-6 w-6 text-purple-600" />
                          <div>
                            <p className="font-semibold text-purple-800">Expert Witness Mode Active</p>
                            <p className="text-sm text-purple-600">
                              Report will include CPR 35 compliance sections, Statement of Truth, and court-ready formatting
                            </p>
                          </div>
                        </>
                      ) : (
                        <>
                          <Shield className="h-6 w-6 text-green-600" />
                          <div>
                            <p className="font-semibold text-green-800">Red Book Compliant Mode</p>
                            <p className="text-sm text-green-600">
                              Report will follow RICS Valuation Global Standards structure
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Valuation Sign-Off Status Card */}
                {valuationStatus && (
                  <Card 
                    className={
                      valuationStatus.status === 'signed_off' 
                        ? 'border-green-300 bg-green-50' 
                        : valuationStatus.status === 'pending_review'
                        ? 'border-amber-300 bg-amber-50'
                        : 'border-blue-300 bg-blue-50'
                    }
                    data-testid="valuation-status-card"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        {valuationStatus.status === 'signed_off' ? (
                          <>
                            <ShieldCheck className="h-6 w-6 text-green-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-green-800">Valuation Signed Off & Locked</p>
                              <p className="text-sm text-green-700 mt-1">
                                Market value of <span className="font-medium">£{valuationStatus.market_value?.toLocaleString()}</span> was 
                                approved by <span className="font-medium">{valuationStatus.signed_off_by_name || 'Director'}</span>
                                {valuationStatus.signed_off_at && (
                                  <> on {new Date(valuationStatus.signed_off_at).toLocaleDateString('en-GB', { 
                                    day: 'numeric', month: 'short', year: 'numeric' 
                                  })}</>
                                )}
                              </p>
                              <div className="mt-2 p-2 bg-green-100 rounded text-xs text-green-700">
                                <Lock className="h-3 w-3 inline mr-1" />
                                Protected fields: Market value, methodology, comparable evidence, basis of value
                              </div>
                            </div>
                          </>
                        ) : valuationStatus.status === 'pending_review' ? (
                          <>
                            <Clock className="h-6 w-6 text-amber-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-amber-800">Valuation Pending Director Review</p>
                              <p className="text-sm text-amber-700 mt-1">
                                Market value of £{valuationStatus.market_value?.toLocaleString()} is awaiting sign-off.
                                Fields can still be edited until approved.
                              </p>
                            </div>
                          </>
                        ) : (
                          <>
                            <Unlock className="h-6 w-6 text-blue-600 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-blue-800">Valuation Draft</p>
                              <p className="text-sm text-blue-700 mt-1">
                                This valuation is in draft status. All fields can be edited.
                                {valuationStatus.market_value > 0 && (
                                  <> Current value: £{valuationStatus.market_value?.toLocaleString()}</>
                                )}
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="flex justify-end">
                  <Button onClick={() => setActiveTab('details')}>
                    Continue to Details
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </TabsContent>

              {/* TAB 2: Report Details */}
              <TabsContent value="details" className="space-y-6 pb-4">
                {/* Screenshot Extractor - Quick Data Import Tool */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    {/* Instructions & Scope */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Instructions & Scope</CardTitle>
                        <CardDescription>RICS VPS 2 - Mandatory requirements</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="client-name">Client Name *</Label>
                            <Input
                              id="client-name"
                              value={clientName}
                              onChange={(e) => setClientName(e.target.value)}
                              placeholder="Enter client name"
                              data-testid="client-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="valuation-purpose">Purpose of Valuation *</Label>
                            <Select value={valuationPurpose} onValueChange={setValuationPurpose}>
                              <SelectTrigger data-testid="purpose-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {VALUATION_PURPOSES.map(p => (
                                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="basis-value">Basis of Value (VPS 4) *</Label>
                            <Select value={basisOfValue} onValueChange={setBasisOfValue}>
                              <SelectTrigger data-testid="basis-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {BASIS_OF_VALUE.map(b => (
                                  <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label htmlFor="report-format">Export Format</Label>
                            <Select value={reportFormat} onValueChange={setReportFormat}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pdf">PDF Document</SelectItem>
                                <SelectItem value="docx">Word Document (.docx)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor="valuation-date">Valuation Date *</Label>
                            <Input
                              id="valuation-date"
                              type="date"
                              value={valuationDate}
                              onChange={(e) => setValuationDate(e.target.value)}
                              data-testid="valuation-date-input"
                            />
                      </div>
                      <div>
                        <Label htmlFor="inspection-date">Inspection Date *</Label>
                        <Input
                          id="inspection-date"
                          type="date"
                          value={inspectionDate}
                          onChange={(e) => setInspectionDate(e.target.value)}
                          data-testid="inspection-date-input"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Executive Summary & Commercial Details */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-5 w-5 text-accent" />
                      Executive Summary
                    </CardTitle>
                    <CardDescription>Key valuation metrics and professional format details</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="report-ref">Report Reference</Label>
                        <Input
                          id="report-ref"
                          value={reportReference}
                          onChange={(e) => setReportReference(e.target.value)}
                          placeholder="e.g., AJA VAL 4655"
                        />
                      </div>
                      <div>
                        <Label htmlFor="client-addressee">Client Contact Name</Label>
                        <Input
                          id="client-addressee"
                          value={clientAddresseeName}
                          onChange={(e) => setClientAddresseeName(e.target.value)}
                          placeholder="e.g., Dr Berrin Tezcan"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="client-address">Client Address</Label>
                      <Textarea
                        id="client-address"
                        value={clientAddress}
                        onChange={(e) => setClientAddress(e.target.value)}
                        placeholder="Full client address..."
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="property-desc">Property Type Description</Label>
                      <Input
                        id="property-desc"
                        value={propertyTypeDescription}
                        onChange={(e) => setPropertyTypeDescription(e.target.value)}
                        placeholder="e.g., Commercial and Residential Valuation Report"
                      />
                    </div>
                    
                    {/* Tenure */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Tenure Type</Label>
                        <Select value={tenureType} onValueChange={setTenureType}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="freehold">Freehold</SelectItem>
                            <SelectItem value="leasehold">Leasehold</SelectItem>
                            <SelectItem value="commonhold">Commonhold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="title-number">Title Number</Label>
                        <Input
                          id="title-number"
                          value={titleNumber}
                          onChange={(e) => setTitleNumber(e.target.value)}
                          placeholder="e.g., BB26210"
                        />
                      </div>
                      {tenureType === 'leasehold' && (
                        <div>
                          <Label htmlFor="lease-term">Lease Term</Label>
                          <Input
                            id="lease-term"
                            value={leaseTerm}
                            onChange={(e) => setLeaseTerm(e.target.value)}
                            placeholder="e.g., 125 years from 4th April 2023"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Valuation Figures */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="rental-value">Rental Value (£)</Label>
                        <Input
                          id="rental-value"
                          type="number"
                          value={rentalValue}
                          onChange={(e) => setRentalValue(e.target.value)}
                          placeholder="e.g., 534046"
                        />
                      </div>
                      <div>
                        <Label htmlFor="capital-value">Capital Value (£)</Label>
                        <Input
                          id="capital-value"
                          type="number"
                          value={capitalValue}
                          onChange={(e) => setCapitalValue(e.target.value)}
                          placeholder="e.g., 9650000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="yield">All Risk Yield (%)</Label>
                        <Input
                          id="yield"
                          type="number"
                          step="0.1"
                          value={allRiskYield}
                          onChange={(e) => setAllRiskYield(e.target.value)}
                          placeholder="e.g., 5.7"
                        />
                      </div>
                    </div>
                    
                    {/* Pros and Cons */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Key Strengths (Pros)</Label>
                        <div className="space-y-2 mt-2">
                          {pros.map((pro, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-green-50 p-2 rounded">
                              <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                              <span className="flex-1 text-sm min-w-0 break-words">{pro}</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 w-6 p-0 flex-shrink-0"
                                onClick={() => setPros(pros.filter((_, i) => i !== idx))}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Input
                              value={newPro}
                              onChange={(e) => setNewPro(e.target.value)}
                              placeholder="Add a strength..."
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && newPro.trim()) {
                                  setPros([...pros, newPro.trim()]);
                                  setNewPro('');
                                }
                              }}
                            />
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (newPro.trim()) {
                                  setPros([...pros, newPro.trim()]);
                                  setNewPro('');
                                }
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label>Key Risks (Cons)</Label>
                        <div className="space-y-2 mt-2">
                          {cons.map((con, idx) => (
                            <div key={idx} className="flex items-center gap-2 bg-red-50 p-2 rounded">
                              <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                              <span className="flex-1 text-sm min-w-0 break-words">{con}</span>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-6 w-6 p-0 flex-shrink-0"
                                onClick={() => setCons(cons.filter((_, i) => i !== idx))}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Input
                              value={newCon}
                              onChange={(e) => setNewCon(e.target.value)}
                              placeholder="Add a risk..."
                              onKeyPress={(e) => {
                                if (e.key === 'Enter' && newCon.trim()) {
                                  setCons([...cons, newCon.trim()]);
                                  setNewCon('');
                                }
                              }}
                            />
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                if (newCon.trim()) {
                                  setCons([...cons, newCon.trim()]);
                                  setNewCon('');
                                }
                              }}
                            >
                              Add
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                  </div>
                  
                  {/* Screenshot Extractor - Right Column */}
                  <div className="col-span-1">
                    <ScreenshotExtractor 
                      onApplyData={handleScreenshotData}
                      disabled={generating}
                    />
                  </div>
                </div>

                {/* CPR 35 Expert Witness Fields */}
                {isExpertWitnessMode && (
                  <Card className="border-purple-200 bg-purple-50/50">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2 text-purple-800">
                        <Gavel className="h-5 w-5" />
                        Expert Witness Requirements (CPR 35)
                      </CardTitle>
                      <CardDescription>Required for court compliance</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label htmlFor="instructing-party">Instructing Party *</Label>
                        <Input
                          id="instructing-party"
                          value={instructingParty}
                          onChange={(e) => setInstructingParty(e.target.value)}
                          placeholder="e.g., Smith & Jones Solicitors (for the Claimant)"
                          data-testid="instructing-party-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="nature-dispute">Nature of Dispute *</Label>
                        <Textarea
                          id="nature-dispute"
                          value={natureOfDispute}
                          onChange={(e) => setNatureOfDispute(e.target.value)}
                          placeholder="Brief description of the dispute..."
                          rows={2}
                          data-testid="dispute-textarea"
                        />
                      </div>
                      <div>
                        <Label htmlFor="questions">Questions to be Addressed</Label>
                        <Textarea
                          id="questions"
                          value={questionsToAddress}
                          onChange={(e) => setQuestionsToAddress(e.target.value)}
                          placeholder="Enter each question on a new line..."
                          rows={3}
                          data-testid="questions-textarea"
                        />
                        <p className="text-xs text-muted-foreground mt-1">One question per line</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Construction Details - Property Description */}
                <Card data-section="construction" data-testid="construction-details-card">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building className="h-5 w-5 text-blue-600" />
                      Construction Details
                    </CardTitle>
                    <CardDescription>Property construction and physical characteristics</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="wall-type">Wall Construction *</Label>
                        <Select value={wallType} onValueChange={setWallType}>
                          <SelectTrigger data-testid="wall-type-select">
                            <SelectValue placeholder="Select wall type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="brick">Solid Brick</SelectItem>
                            <SelectItem value="cavity_brick">Cavity Brick</SelectItem>
                            <SelectItem value="stone">Stone</SelectItem>
                            <SelectItem value="rendered">Rendered</SelectItem>
                            <SelectItem value="timber_frame">Timber Frame</SelectItem>
                            <SelectItem value="concrete">Concrete</SelectItem>
                            <SelectItem value="steel_frame">Steel Frame</SelectItem>
                            <SelectItem value="mixed">Mixed Construction</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="roof-type">Roof Type *</Label>
                        <Select value={roofType} onValueChange={setRoofType}>
                          <SelectTrigger data-testid="roof-type-select">
                            <SelectValue placeholder="Select roof type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pitched_tile">Pitched - Tile</SelectItem>
                            <SelectItem value="pitched_slate">Pitched - Slate</SelectItem>
                            <SelectItem value="pitched_concrete">Pitched - Concrete Tile</SelectItem>
                            <SelectItem value="flat_felt">Flat - Felt</SelectItem>
                            <SelectItem value="flat_membrane">Flat - Membrane</SelectItem>
                            <SelectItem value="flat_grp">Flat - GRP</SelectItem>
                            <SelectItem value="metal">Metal</SelectItem>
                            <SelectItem value="thatch">Thatch</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="window-type">Window Type</Label>
                        <Select value={windowType} onValueChange={setWindowType}>
                          <SelectTrigger data-testid="window-type-select">
                            <SelectValue placeholder="Select window type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="upvc_double">uPVC Double Glazed</SelectItem>
                            <SelectItem value="upvc_single">uPVC Single Glazed</SelectItem>
                            <SelectItem value="timber_double">Timber Double Glazed</SelectItem>
                            <SelectItem value="timber_single">Timber Single Glazed</SelectItem>
                            <SelectItem value="aluminium">Aluminium</SelectItem>
                            <SelectItem value="sash">Sash Windows</SelectItem>
                            <SelectItem value="mixed">Mixed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="approximate-age">Approximate Age/Period *</Label>
                        <Select value={approximateAge} onValueChange={setApproximateAge}>
                          <SelectTrigger data-testid="age-select">
                            <SelectValue placeholder="Select period" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pre_1900">Pre-1900 (Victorian/Earlier)</SelectItem>
                            <SelectItem value="1900_1929">1900-1929 (Edwardian)</SelectItem>
                            <SelectItem value="1930_1949">1930-1949 (Inter-War)</SelectItem>
                            <SelectItem value="1950_1969">1950-1969 (Post-War)</SelectItem>
                            <SelectItem value="1970_1989">1970-1989</SelectItem>
                            <SelectItem value="1990_2009">1990-2009</SelectItem>
                            <SelectItem value="2010_present">2010-Present (Modern)</SelectItem>
                            <SelectItem value="new_build">New Build</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="floor-area">Gross Internal Area (m²) *</Label>
                        <Input
                          id="floor-area"
                          type="number"
                          value={floorAreaSqm}
                          onChange={(e) => setFloorAreaSqm(e.target.value)}
                          placeholder="e.g., 120"
                          data-testid="floor-area-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="tenure-type-construction">Tenure</Label>
                        <Select value={tenureType} onValueChange={setTenureType}>
                          <SelectTrigger data-testid="tenure-select">
                            <SelectValue placeholder="Select tenure" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="freehold">Freehold</SelectItem>
                            <SelectItem value="leasehold">Leasehold</SelectItem>
                            <SelectItem value="share_of_freehold">Share of Freehold</SelectItem>
                            <SelectItem value="commonhold">Commonhold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="construction-notes">Additional Construction Notes</Label>
                      <Textarea
                        id="construction-notes"
                        value={constructionNotes}
                        onChange={(e) => setConstructionNotes(e.target.value)}
                        placeholder="Any additional notes about the property construction, condition, extensions, alterations..."
                        rows={2}
                        data-testid="construction-notes"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Comparable Analysis Matrix */}
                <ComparableAnalysisMatrix
                  inspectionId={inspection?.id}
                  subjectProperty={{
                    address: inspection?.property_address,
                    property_type: propertyDetails?.property_type,
                    age: approximateAge,
                    bedrooms: propertyDetails?.bedrooms,
                    reception_rooms: propertyDetails?.reception_rooms,
                    bathrooms: propertyDetails?.bathrooms,
                    floor_area_sqm: floorAreaSqm ? parseFloat(floorAreaSqm) : null,
                    tenure: tenureType,
                    condition: propertyDetails?.condition
                  }}
                  onValuationCalculated={(calc) => {
                    // Auto-populate market value from calculation if not set
                    if (!marketValue && calc.weighted_average) {
                      setMarketValue(calc.weighted_average.toString());
                    }
                  }}
                  disabled={generating}
                />

                {/* Investment Report Intelligence - For Investment/Commercial/Healthcare Reports */}
                <InvestmentReportIntelligence
                  inspectionId={inspection?.id}
                  reportId={complianceReport?.id}
                  reportType={reportType}
                  token={token}
                  onUpdate={(intelligence) => {
                    // Update local state when intelligence changes
                    console.log('Report intelligence updated:', intelligence.id);
                  }}
                />

                {/* Valuation Methodology */}
                <Card data-section="methodology" className={editSection === 'methodology' ? 'ring-2 ring-red-500' : ''}>
                  <CardHeader>
                    <CardTitle className="text-base">Valuation Methodology</CardTitle>
                    <CardDescription>VPS 4 - Method and approach used</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="valuation-method">Valuation Method *</Label>
                      <Select value={valuationMethod} onValueChange={setValuationMethod}>
                        <SelectTrigger data-testid="valuation-method">
                          <SelectValue placeholder="Select method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="comparable">Comparable Method</SelectItem>
                          <SelectItem value="investment">Investment Method</SelectItem>
                          <SelectItem value="residual">Residual Method</SelectItem>
                          <SelectItem value="drc">Depreciated Replacement Cost (DRC)</SelectItem>
                          <SelectItem value="profits">Profits Method</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="method-description">Method Description</Label>
                      <Textarea
                        id="method-description"
                        value={valuationMethodDescription}
                        onChange={(e) => setValuationMethodDescription(e.target.value)}
                        placeholder="Describe how this method was applied..."
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Valuation Rationale */}
                <Card data-section="rationale" className={editSection === 'rationale' ? 'ring-2 ring-red-500' : ''}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">Valuation Rationale</CardTitle>
                        <CardDescription>Logical justification for the valuation</CardDescription>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={generateAIContent}
                        disabled={generatingAI || !complianceReport || !valuationMethod}
                        className="gap-2"
                        data-testid="ai-generate-btn"
                      >
                        {generatingAI ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-4 w-4" />
                            AI Generate
                          </>
                        )}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="valuation-rationale">Rationale & Justification *</Label>
                      <Textarea
                        id="valuation-rationale"
                        value={valuationRationale}
                        onChange={(e) => setValuationRationale(e.target.value)}
                        placeholder="Provide clear logical justification for your valuation... Or click 'AI Generate' above"
                        rows={4}
                        data-testid="valuation-rationale"
                      />
                      <p className="text-xs text-muted-foreground mt-1">AI-generated content can be freely edited</p>
                    </div>
                    <div>
                      <Label htmlFor="judgement-commentary">Judgement Commentary</Label>
                      <Textarea
                        id="judgement-commentary"
                        value={judgementCommentary}
                        onChange={(e) => setJudgementCommentary(e.target.value)}
                        placeholder="Commentary on professional judgement applied..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="market-evidence">Market Evidence Summary</Label>
                      <Textarea
                        id="market-evidence"
                        value={marketEvidence}
                        onChange={(e) => setMarketEvidence(e.target.value)}
                        placeholder="Summary of market evidence considered..."
                        rows={3}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Valuation Conclusion */}
                <Card data-section="conclusion" className={editSection === 'conclusion' ? 'ring-2 ring-red-500' : ''}>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-emerald-500" />
                      Valuation Conclusion
                    </CardTitle>
                    <CardDescription>Final valuation figure</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="market-value">Market Value (£) *</Label>
                        <div className="relative">
                          <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="market-value"
                            type="number"
                            value={marketValue}
                            onChange={(e) => setMarketValue(e.target.value)}
                            placeholder="Enter value"
                            className="pl-9"
                            data-testid="market-value"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Value in Words</Label>
                        <p className="text-sm p-2 bg-muted/30 rounded mt-1">
                          {marketValue ? `${numberToWords(parseInt(marketValue))} pounds` : 'Enter value to see'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Valuer Declaration */}
                <Card data-section="valuer" className={editSection === 'valuer' ? 'ring-2 ring-red-500' : ''}>
                  <CardHeader>
                    <CardTitle className="text-base">Valuer Declaration</CardTitle>
                    <CardDescription>VPS 3 - Valuer qualifications and status</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="valuer-name">Valuer Name *</Label>
                        <Input
                          id="valuer-name"
                          value={valuerName}
                          onChange={(e) => setValuerName(e.target.value)}
                          placeholder="Full name"
                          data-testid="valuer-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="valuer-qual">Qualification *</Label>
                        <Select value={valuerQualification} onValueChange={setValuerQualification}>
                          <SelectTrigger data-testid="valuer-qualification">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="MRICS">MRICS</SelectItem>
                            <SelectItem value="FRICS">FRICS</SelectItem>
                            <SelectItem value="AssocRICS">AssocRICS</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="pi-insurance">PI Insurance Confirmation *</Label>
                      <Textarea
                        id="pi-insurance"
                        value={piInsurance}
                        onChange={(e) => setPiInsurance(e.target.value)}
                        rows={2}
                        data-testid="pi-insurance"
                      />
                    </div>
                  </CardContent>
                </Card>

                <div className="flex justify-between items-center">
                  <Button variant="outline" onClick={() => setActiveTab('type')}>
                    Back
                  </Button>
                  <div className="flex items-center gap-3">
                    {!complianceReport ? (
                      // Show Create button if no report exists
                      <>
                        {!canProceed() && (
                          <span className="text-sm text-muted-foreground">
                            {!clientName ? 'Client Name required' : 
                             !valuationDate || !inspectionDate ? 'Dates required' :
                             isExpertWitnessMode && !instructingParty ? 'Instructing Party required' :
                             isExpertWitnessMode && !natureOfDispute ? 'Nature of Dispute required' : ''}
                          </span>
                        )}
                        <Button 
                          onClick={createComplianceReport} 
                          disabled={generating || !canProceed()}
                          data-testid="create-report-btn"
                        >
                          {generating ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Creating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Create Compliance Report
                            </>
                          )}
                        </Button>
                      </>
                    ) : (
                      // Show Update and Re-validate buttons if report exists
                      <>
                        <Button 
                          variant="outline"
                          onClick={() => setActiveTab('validate')}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Validation
                        </Button>
                        <Button 
                          onClick={updateComplianceReport} 
                          disabled={generating}
                          data-testid="update-report-btn"
                        >
                          {generating ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Save & Re-validate
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* TAB 3: Preview */}
              <TabsContent value="preview" className="space-y-6 pb-4">
                {!complianceReport ? (
                  <Card className="border-yellow-200 bg-yellow-50">
                    <CardContent className="py-8 text-center">
                      <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-yellow-500" />
                      <p className="font-medium">No Report Created Yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Complete the Details tab to create your compliance report
                      </p>
                      <Button variant="outline" className="mt-4" onClick={() => setActiveTab('details')}>
                        Go to Details
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Report Header */}
                    <div className={`rounded-lg p-6 ${isExpertWitnessMode ? 'bg-gradient-to-r from-purple-800 to-purple-900' : 'bg-gradient-to-r from-emerald-700 to-emerald-900'} text-white`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="bg-white/20 text-white">
                          {isExpertWitnessMode ? 'CPR 35 Expert Report' : 'RICS Red Book Valuation'}
                        </Badge>
                        <Badge className="bg-white/20 text-white">
                          {BASIS_OF_VALUE.find(b => b.value === basisOfValue)?.label}
                        </Badge>
                      </div>
                      <h2 className="text-2xl font-bold mb-2">
                        {isValuationReport ? 'Valuation Report' : 'Survey Report'}
                      </h2>
                      <p className="text-white/80 flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        {inspection?.property_address}
                      </p>
                      <div className="flex flex-wrap gap-4 mt-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-white/60" />
                          Valuation Date: {valuationDate && !isNaN(new Date(valuationDate).getTime()) ? new Date(valuationDate).toLocaleDateString('en-GB') : 'Not set'}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4 text-white/60" />
                          Client: {clientName}
                        </span>
                      </div>
                    </div>

                    {/* Section Structure Preview */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Report Structure</CardTitle>
                        <CardDescription>Sections included based on report type</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {/* Red Book Sections */}
                          {['Executive Summary', 'Instructions & Scope', 'Basis of Value', 'Property Description', 
                            'Tenure & Legal Matters', 'Planning & Statutory', 'Market Commentary', 
                            'Valuation Methodology', 'Valuation Rationale', 'Valuation Conclusion',
                            'Assumptions & Limiting Conditions', 'RICS Compliance Statement', 'Valuer Declaration'
                          ].map((section, idx) => (
                            <div key={section} className="flex items-center gap-3 p-2 bg-muted/30 rounded">
                              <span className="text-xs font-mono text-muted-foreground w-6">{idx + 1}.</span>
                              <CheckCircle className="h-4 w-4 text-green-500" />
                              <span className="text-sm">{section}</span>
                            </div>
                          ))}
                          
                          {/* CPR 35 Sections */}
                          {isExpertWitnessMode && (
                            <>
                              <Separator className="my-3" />
                              <p className="text-sm font-medium text-purple-700 flex items-center gap-2">
                                <Gavel className="h-4 w-4" />
                                CPR 35 Additional Sections
                              </p>
                              {['Instructions to Expert', "Expert's Duty to Court", 'Documents Relied Upon',
                                'Facts & Assumptions', 'Matters Outside Expertise', 'Statement of Truth', 
                                'Independence Declaration'
                              ].map((section, idx) => (
                                <div key={section} className="flex items-center gap-3 p-2 bg-purple-50 rounded">
                                  <span className="text-xs font-mono text-purple-400">A{idx + 1}.</span>
                                  <CheckCircle className="h-4 w-4 text-purple-500" />
                                  <span className="text-sm text-purple-800">{section}</span>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Valuation Summary */}
                    {isValuationReport && inspection?.valuation_amount && (
                      <Card className="border-emerald-200">
                        <CardHeader>
                          <CardTitle className="text-base flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-emerald-500" />
                            Valuation Conclusion
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="text-center p-6 bg-emerald-50 rounded-lg border border-emerald-200">
                            <p className="text-sm text-emerald-600 mb-1">Market Value</p>
                            <p className="text-4xl font-bold text-emerald-700">
                              £{inspection.valuation_amount.toLocaleString()}
                            </p>
                            <p className="text-sm font-bold text-emerald-800 mt-2 uppercase">
                              ({numberToWords(inspection.valuation_amount).toUpperCase()} POUNDS)
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Accommodation Schedule */}
                    <Card>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base flex items-center gap-2">
                              <Home className="h-5 w-5 text-blue-500" />
                              Accommodation Schedule
                            </CardTitle>
                            <CardDescription>
                              Room details from property inspection
                            </CardDescription>
                          </div>
                          <div className="flex gap-2">
                            {editingAccommodation ? (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={cancelAccommodationEdit}
                                  disabled={savingAccommodation}
                                  data-testid="cancel-accommodation-btn"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={saveAccommodation}
                                  disabled={savingAccommodation}
                                  data-testid="save-accommodation-btn"
                                >
                                  {savingAccommodation ? (
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                  ) : (
                                    <Save className="h-4 w-4 mr-1" />
                                  )}
                                  Save
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setEditingAccommodation(true)}
                                data-testid="edit-accommodation-btn"
                              >
                                <Pencil className="h-4 w-4 mr-1" />
                                Edit
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {/* Summary Stats - always show based on current accommodation state */}
                        {accommodation.length > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                            <div className="bg-blue-50 p-3 rounded-lg text-center">
                              <p className="text-2xl font-bold text-blue-700">
                                {accommodation.filter(r => 
                                  ['bedroom', 'master_bedroom', 'double_bedroom', 'single_bedroom', 'box_room'].includes(r.type)
                                ).length}
                              </p>
                              <p className="text-xs text-blue-600">Bedrooms</p>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg text-center">
                              <p className="text-2xl font-bold text-green-700">
                                {accommodation.filter(r => 
                                  ['reception', 'living_room', 'lounge', 'sitting_room', 'dining_room'].includes(r.type)
                                ).length}
                              </p>
                              <p className="text-xs text-green-600">Receptions</p>
                            </div>
                            <div className="bg-purple-50 p-3 rounded-lg text-center">
                              <p className="text-2xl font-bold text-purple-700">
                                {accommodation.filter(r => 
                                  ['bathroom', 'shower_room', 'en_suite', 'wc', 'cloakroom'].includes(r.type)
                                ).length}
                              </p>
                              <p className="text-xs text-purple-600">Bathrooms</p>
                            </div>
                            <div className="bg-amber-50 p-3 rounded-lg text-center">
                              <p className="text-2xl font-bold text-amber-700">
                                {accommodation.reduce((sum, r) => sum + ((r.length || 0) * (r.width || 0)), 0).toFixed(0)}
                              </p>
                              <p className="text-xs text-amber-600">Total sqm</p>
                            </div>
                          </div>
                        )}

                        {/* Editable Table or Read-only Display */}
                        {editingAccommodation ? (
                          <div className="space-y-3">
                            {accommodation.length > 0 ? (
                              <div className="border rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted">
                                    <tr>
                                      <th className="px-2 py-2 text-left font-medium">Name</th>
                                      <th className="px-2 py-2 text-left font-medium">Type</th>
                                      <th className="px-2 py-2 text-left font-medium">Floor</th>
                                      <th className="px-2 py-2 text-left font-medium w-20">L (m)</th>
                                      <th className="px-2 py-2 text-left font-medium w-20">W (m)</th>
                                      <th className="px-2 py-2 text-left font-medium w-16">Area</th>
                                      <th className="px-2 py-2 text-center font-medium w-12"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {accommodation.map((room, idx) => (
                                      <tr key={room.id || idx} className="border-t">
                                        <td className="px-2 py-1">
                                          <Input
                                            value={room.name || ''}
                                            onChange={(e) => updateRoom(idx, 'name', e.target.value)}
                                            className="h-8 text-sm"
                                            placeholder="Room name"
                                            data-testid={`room-name-${idx}`}
                                          />
                                        </td>
                                        <td className="px-2 py-1">
                                          <Select
                                            value={room.type || 'bedroom'}
                                            onValueChange={(value) => updateRoom(idx, 'type', value)}
                                          >
                                            <SelectTrigger className="h-8 text-sm" data-testid={`room-type-${idx}`}>
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {ROOM_TYPES.map((type) => (
                                                <SelectItem key={type.value} value={type.value}>
                                                  {type.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </td>
                                        <td className="px-2 py-1">
                                          <Select
                                            value={room.floor || 'Ground'}
                                            onValueChange={(value) => updateRoom(idx, 'floor', value)}
                                          >
                                            <SelectTrigger className="h-8 text-sm" data-testid={`room-floor-${idx}`}>
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {FLOOR_OPTIONS.map((floor) => (
                                                <SelectItem key={floor.value} value={floor.value}>
                                                  {floor.label}
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </td>
                                        <td className="px-2 py-1">
                                          <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={room.length || ''}
                                            onChange={(e) => updateRoom(idx, 'length', parseFloat(e.target.value) || 0)}
                                            className="h-8 text-sm w-16"
                                            placeholder="0"
                                            data-testid={`room-length-${idx}`}
                                          />
                                        </td>
                                        <td className="px-2 py-1">
                                          <Input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={room.width || ''}
                                            onChange={(e) => updateRoom(idx, 'width', parseFloat(e.target.value) || 0)}
                                            className="h-8 text-sm w-16"
                                            placeholder="0"
                                            data-testid={`room-width-${idx}`}
                                          />
                                        </td>
                                        <td className="px-2 py-1 text-center font-medium text-muted-foreground">
                                          {((room.length || 0) * (room.width || 0)).toFixed(1)}
                                        </td>
                                        <td className="px-2 py-1 text-center">
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => removeRoom(idx)}
                                            data-testid={`remove-room-${idx}`}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="text-center text-muted-foreground py-4">
                                No rooms added yet. Click "Add Room" to begin.
                              </p>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={addRoom}
                              className="w-full"
                              data-testid="add-room-btn"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Room
                            </Button>
                          </div>
                        ) : (
                          // Read-only display
                          accommodation.length > 0 ? (
                            <div className="border rounded-lg overflow-hidden">
                              <table className="w-full text-sm">
                                <thead className="bg-muted">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium">Room</th>
                                    <th className="px-3 py-2 text-left font-medium">Floor</th>
                                    <th className="px-3 py-2 text-left font-medium">Size</th>
                                    <th className="px-3 py-2 text-left font-medium">Area</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {accommodation.map((room, idx) => (
                                    <tr key={room.id || idx} className="border-t">
                                      <td className="px-3 py-2">
                                        <span className="font-medium">{room.name}</span>
                                        <span className="text-muted-foreground ml-1">
                                          ({ROOM_TYPES.find(t => t.value === room.type)?.label || room.type?.replace('_', ' ')})
                                        </span>
                                      </td>
                                      <td className="px-3 py-2">{room.floor || 'Ground'}</td>
                                      <td className="px-3 py-2">{room.length || 0}m × {room.width || 0}m</td>
                                      <td className="px-3 py-2 font-medium">
                                        {((room.length || 0) * (room.width || 0)).toFixed(1)} sqm
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-6 text-muted-foreground">
                              <Home className="h-10 w-10 mx-auto mb-2 opacity-50" />
                              <p>No accommodation data recorded</p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  addRoom();
                                }}
                                className="mt-3"
                                data-testid="add-first-room-btn"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Rooms
                              </Button>
                            </div>
                          )
                        )}
                      </CardContent>
                    </Card>

                    <div className="flex justify-between">
                      <Button variant="outline" onClick={() => setActiveTab('details')}>
                        Edit Details
                      </Button>
                      <Button onClick={() => setActiveTab('validate')}>
                        Validate Compliance
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* TAB 4: Validation */}
              <TabsContent value="validate" className="space-y-6 pb-4">
                {!complianceReport ? (
                  <Card className="border-yellow-200 bg-yellow-50">
                    <CardContent className="py-8 text-center">
                      <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-yellow-500" />
                      <p className="font-medium">No Report to Validate</p>
                      <Button variant="outline" className="mt-4" onClick={() => setActiveTab('details')}>
                        Create Report First
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    {/* Validation Status */}
                    <Card className={validationResult?.blocking_issues?.length > 0 
                      ? 'border-red-300 bg-red-50' 
                      : validationResult?.is_compliant 
                        ? 'border-green-300 bg-green-50'
                        : 'border-gray-200'
                    }>
                      <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                          {validationResult?.blocking_issues?.length > 0 ? (
                            <>
                              <XCircle className="h-12 w-12 text-red-500" />
                              <div>
                                <p className="text-lg font-semibold text-red-800">Compliance Issues Found</p>
                                <p className="text-sm text-red-600">
                                  {validationResult.blocking_issues.length} blocking issue(s) must be resolved
                                </p>
                              </div>
                            </>
                          ) : validationResult ? (
                            <>
                              <CheckCircle className="h-12 w-12 text-green-500" />
                              <div>
                                <p className="text-lg font-semibold text-green-800">Compliance Passed</p>
                                <p className="text-sm text-green-600">
                                  Report meets all {isExpertWitnessMode ? 'Red Book & CPR 35' : 'Red Book'} requirements
                                </p>
                              </div>
                            </>
                          ) : (
                            <>
                              <Info className="h-12 w-12 text-gray-400" />
                              <div>
                                <p className="text-lg font-semibold">Ready to Validate</p>
                                <p className="text-sm text-muted-foreground">
                                  Run compliance checks before generating final report
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Validation Checklist */}
                    {validationResult && (
                      <>
                        {/* Red Book Checks */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base flex items-center gap-2">
                              <Shield className="h-5 w-5 text-green-500" />
                              RICS Red Book Compliance
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              {validationResult.red_book_checks?.map((check) => (
                                <div 
                                  key={check.check_id} 
                                  className={`flex items-center gap-3 p-2 rounded transition-all ${
                                    check.is_passed ? 'bg-green-50' : 'bg-red-50 hover:bg-red-100 cursor-pointer'
                                  }`}
                                  onClick={() => !check.is_passed && handleFixIssue(check.check_id)}
                                >
                                  {check.is_passed ? (
                                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                                  ) : (
                                    <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                  )}
                                  <div className="flex-1">
                                    <span className="text-sm">{check.description}</span>
                                    {!check.is_passed && (
                                      <p className="text-xs text-red-600 mt-0.5">{check.failure_reason}</p>
                                    )}
                                  </div>
                                  {!check.is_passed ? (
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 text-red-600 hover:text-red-700 hover:bg-red-200"
                                      onClick={(e) => { e.stopPropagation(); handleFixIssue(check.check_id); }}
                                    >
                                      <Edit className="h-3 w-3 mr-1" />
                                      Fix
                                    </Button>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">[{check.check_id}]</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>

                        {/* CPR 35 Checks */}
                        {isExpertWitnessMode && validationResult.cpr35_checks?.length > 0 && (
                          <Card>
                            <CardHeader>
                              <CardTitle className="text-base flex items-center gap-2">
                                <Gavel className="h-5 w-5 text-purple-500" />
                                CPR 35 Expert Witness Compliance
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {validationResult.cpr35_checks?.map((check) => (
                                  <div 
                                    key={check.check_id} 
                                    className={`flex items-center gap-3 p-2 rounded transition-all ${
                                      check.is_passed ? 'bg-purple-50' : 'bg-red-50 hover:bg-red-100 cursor-pointer'
                                    }`}
                                    onClick={() => !check.is_passed && handleFixIssue(check.check_id)}
                                  >
                                    {check.is_passed ? (
                                      <CheckCircle className="h-4 w-4 text-purple-500 flex-shrink-0" />
                                    ) : (
                                      <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                    )}
                                    <div className="flex-1">
                                      <span className="text-sm">{check.description}</span>
                                      {!check.is_passed && (
                                        <p className="text-xs text-red-600 mt-0.5">{check.failure_reason}</p>
                                      )}
                                    </div>
                                    {!check.is_passed ? (
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        className="h-7 text-red-600 hover:text-red-700 hover:bg-red-200"
                                        onClick={(e) => { e.stopPropagation(); handleFixIssue(check.check_id); }}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Fix
                                      </Button>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">[{check.check_id}]</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </>
                    )}

                    {/* Version Options */}
                    <Card className="bg-gray-50">
                      <CardContent className="py-4">
                        <div className="flex items-center gap-6">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">Generate as:</span>
                            <div className="flex items-center gap-2">
                              <Button
                                variant={isDraft ? "default" : "outline"}
                                size="sm"
                                onClick={() => setIsDraft(true)}
                                className={isDraft ? "bg-amber-500 hover:bg-amber-600" : ""}
                              >
                                Draft
                              </Button>
                              <Button
                                variant={!isDraft ? "default" : "outline"}
                                size="sm"
                                onClick={() => setIsDraft(false)}
                                className={!isDraft ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                              >
                                Final
                              </Button>
                            </div>
                          </div>
                          <div className="flex-1">
                            <Input
                              value={versionNotes}
                              onChange={(e) => setVersionNotes(e.target.value)}
                              placeholder="Version notes (optional)..."
                              className="text-sm"
                            />
                          </div>
                        </div>
                        {isDraft && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Draft reports include a "DRAFT" watermark (if enabled in letterhead settings)
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-4">
                      <Button 
                        variant="outline" 
                        onClick={validateCompliance}
                        disabled={validating}
                      >
                        {validating ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Validating...
                          </>
                        ) : (
                          <>
                            <Shield className="h-4 w-4 mr-2" />
                            Run Validation
                          </>
                        )}
                      </Button>
                      
                      <Button 
                        onClick={generateAndDownload}
                        disabled={generating || (validationResult?.blocking_issues?.length > 0)}
                        data-testid="generate-final-btn"
                        className={isDraft ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-500 hover:bg-emerald-600"}
                      >
                        {generating ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FileDown className="h-4 w-4 mr-2" />
                            Generate {isDraft ? 'Draft' : 'Final'}
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* TAB 5: Version History */}
              <TabsContent value="versions" className="space-y-4 pb-4 mt-0">
                {complianceReport?.id ? (
                  <ReportVersionHistory reportId={complianceReport.id} />
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p className="text-muted-foreground">
                        Save the report first to track versions
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Go to the Validate tab and generate a report to create the first version
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
              </div>
            </div>
          )}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
